import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

import { WelcomeSection, NavigationButtons, ProfileFooter } from './components';
import {
  ProfileNavigationProvider,
  useProfileNavigation,
} from './contexts/ProfileNavigationContext';

const routeToLabel: Record<string, import('./contexts/ProfileNavigationContext').ProfilePage> = {
  '': 'Home',
  'profile': 'Profile',
  'your-plans': 'Your plans',
  'billing': 'Billing',
  'faqs': 'FAQs',
  'resources': 'Resources',
  'id-cards': 'ID Cards',
};

const labelToRoute: Record<import('./contexts/ProfileNavigationContext').ProfilePage, string> = {
  'Home': '',
  'Profile': 'profile',
  'Your plans': 'your-plans',
  'Billing': 'billing',
  'FAQs': 'faqs',
  'Resources': 'resources',
  'ID Cards': 'id-cards',
};

const ProfileContent = () => {
  const { activePage, setActivePage } = useProfileNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = React.useRef(true);

  // Sync active tab from URL on location change
  React.useEffect(() => {
    // Expecting paths like /healthhub, /healthhub/profile, /healthhub/your-plans, etc.
    const segments = location.pathname.split('/').filter(Boolean);
    const afterHealthhub = segments[1] || '';
    const mapped = routeToLabel[afterHealthhub as keyof typeof routeToLabel] || 'Home';
    if (mapped !== activePage) {
      setActivePage(mapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Navigate when active tab changes via UI controls
  React.useEffect(() => {
    // Skip navigation on initial mount to prevent redirect loops
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    const target = labelToRoute[activePage];
    const base = '/healthhub';
    const nextPath = target ? `${base}/${target}` : base;
    if (location.pathname !== nextPath) {
      navigate(nextPath, { replace: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePage]);

  return (
    <div className="profile flex flex-col gap-8 mt-7 max-md:w-full">
      <WelcomeSection />
      <NavigationButtons />
      <Outlet />
      <ProfileFooter />
    </div>
  );
};

export const Profile = () => {
  return (
    <ProfileNavigationProvider>
      <ProfileContent />
    </ProfileNavigationProvider>
  );
};
